// frontend/src/components/Sidebar.js
import React, { useState, useEffect, useMemo } from 'react';
import { 
    TrendingUp, DollarSign, CreditCard, LogOut, Bot, User, 
    Calendar as CalendarIcon, KeyRound, Shield, Settings, 
    FileText, FileClock, ChevronsUpDown, ChevronDown, Users, 
    ClipboardList, DatabaseBackup, Bell, BellRing, Briefcase,
    ArrowDownCircle, ArrowUpCircle, Palette, Table, Activity, FileSpreadsheet,
    Package, List,
    Warehouse, Search, Replace, Trash2, ClipboardCheck,
    ShoppingCart, Handshake, FilePlus, Banknote, FileDown,
    Truck, Fuel, BadgeDollarSign // BadgeDollarSign importado para o ícone de Comissões
} from 'lucide-react';

// --- COMPONENTE: BARRA SUPERIOR MOBILE (Com Troca de Entidade) ---
const MobileHeader = ({ logoUrl, entityName, user, onLogout, selectedCurrency, setSelectedCurrency, allCurrencies, onSwitchEntity, entities, isSwitchingEntity }) => {
    const [isProfileOpen, setIsProfileOpen] = useState(false);

    return (
        <div className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-40 flex items-center justify-between px-4 shadow-sm">
            {/* Logo e Nome */}
            <div className="flex items-center gap-2 overflow-hidden">
                {logoUrl ? (
                    <img src={logoUrl} alt="Logo" className="w-8 h-8 object-contain" />
                ) : (
                    <div className="p-1.5 bg-blue-600 rounded-lg">
                        <DollarSign className="w-4 h-4 text-white" />
                    </div>
                )}
                <span className="font-bold text-gray-800 truncate text-sm max-w-[120px]">{entityName}</span>
            </div>

            {/* Ações Direita: Moeda e Perfil */}
            <div className="flex items-center gap-3">
                {/* Seletor de Moeda Mini */}
                <select
                    value={selectedCurrency}
                    onChange={(e) => setSelectedCurrency(e.target.value)}
                    className="text-xs bg-gray-50 border border-gray-200 rounded p-1 max-w-[60px]"
                >
                    {allCurrencies.map(c => (
                        <option key={c.code} value={c.code}>{c.code}</option>
                    ))}
                </select>

                {/* Avatar / Menu Perfil */}
                <div className="relative">
                    <button onClick={() => setIsProfileOpen(!isProfileOpen)} className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-xs">
                        {user.username.substring(0, 2).toUpperCase()}
                    </button>

                    {isProfileOpen && (
                        <div className="absolute right-0 top-10 w-56 bg-white rounded-lg shadow-xl border border-gray-200 p-2 animate-in fade-in slide-in-from-top-2">
                            <div className="px-2 py-1 border-b border-gray-100 mb-1">
                                <p className="text-xs font-bold text-gray-500">{user.username}</p>
                                <p className="text-[10px] text-gray-400">{user.entityName}</p>
                            </div>

                            {/* --- SELETOR DE ENTIDADE --- */}
                            {user.is_super_user && Array.isArray(entities) && entities.length > 1 && (
                                <div className="my-1 pt-2 border-t border-gray-100 px-2 pb-1">
                                    <label className="block px-1 mb-1.5 text-[10px] font-bold text-gray-400 uppercase">Trocar Entidade</label>
                                    <select
                                        value={user.entityId}
                                        onChange={(e) => {
                                            onSwitchEntity(Number(e.target.value));
                                            setIsProfileOpen(false);
                                        }}
                                        disabled={isSwitchingEntity}
                                        className="w-full p-1.5 text-xs bg-gray-50 border border-gray-200 rounded text-gray-700 focus:ring-blue-500"
                                    >
                                        {entities.map(entity => (
                                            <option key={entity.id} value={entity.id}>
                                                {entity.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="my-1 border-t border-gray-100"></div>
                            <button onClick={onLogout} className="flex items-center w-full gap-2 px-2 py-2 text-sm text-red-600 rounded hover:bg-red-50">
                                <LogOut size={14} /> Sair
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- COMPONENTE: BARRA INFERIOR MOBILE (Bottom Navigation) ---
const MobileBottomNav = ({ items, currentPage, setCurrentPage, unreadCount }) => {
    return (
        <div className="fixed bottom-0 left-0 right-0 h-16 bg-white border-t border-gray-200 z-50 flex justify-around items-center px-1 pb-safe overflow-x-auto scrollbar-hide">
            {items.map(item => {
                const isActive = currentPage === item.id;
                return (
                    <button
                        key={item.id}
                        onClick={() => setCurrentPage(item.id)}
                        className={`flex flex-col items-center justify-center w-full h-full min-w-[60px] space-y-1 ${isActive ? 'text-blue-600' : 'text-gray-400'}`}
                    >
                        <div className="relative">
                            <item.icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                            {item.id === 'meus-avisos' && unreadCount > 0 && (
                                <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                                </span>
                            )}
                        </div>
                        <span className="text-[10px] font-medium truncate max-w-[64px]">{item.label}</span>
                    </button>
                );
            })}
        </div>
    );
};

// --- COMPONENTE INTERNO PARA O FILTRO DE MOEDA (DESKTOP) ---
const CurrencyFilter = ({ currentUser, allCurrencies, selectedCurrency, setSelectedCurrency }) => {
    const currencyMap = useMemo(() => new Map((allCurrencies || []).map(c => [c.code, c])), [allCurrencies]);

    const permittedCurrencies = useMemo(() => {
        return (currentUser.allowedCurrencies || [])
            .map(code => currencyMap.get(code))
            .filter(Boolean);
    }, [currentUser.allowedCurrencies, currencyMap]);

    if (permittedCurrencies.length <= 1) return null;

    return (
        <div className="px-4 py-4 border-b border-gray-200 bg-white">
            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">Moeda</label>
            <div className="relative">
                <select
                    value={selectedCurrency}
                    onChange={(e) => setSelectedCurrency(e.target.value)}
                    className="w-full p-2.5 pl-3 text-sm bg-gray-50 border border-gray-300 text-gray-700 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
                >
                    {permittedCurrencies.map(currency => (
                        <option key={currency.code} value={currency.code}>
                            {`${currency.symbol} ${currency.code} - ${currency.name}`}
                        </option>
                    ))}
                </select>
                <ChevronsUpDown className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
        </div>
    );
};

// --- PERFIL DE USUÁRIO (DESKTOP) ---
const UserProfile = ({ user, onLogout, onChangePassword, onChangeKeyword, onSwitchEntity, entities, isSwitchingEntity }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    return (
        <div className="relative p-4 border-t border-gray-200 bg-white">
            <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center w-full p-2 space-x-3 text-left rounded-lg hover:bg-gray-100 transition-colors group"
            >
                <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-full shadow-md group-hover:ring-2 group-hover:ring-blue-500 transition-all">
                    <span className="text-white font-bold text-sm">{user.username.substring(0, 2).toUpperCase()}</span>
                </div>
                <div className="hidden md:block flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{user.username}</p>
                    <p className="text-xs text-gray-500 truncate">{user.entityName}</p>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {isMenuOpen && (
                <div className="absolute left-2 right-2 bottom-20 z-50 p-1 bg-white rounded-xl shadow-xl border border-gray-200 animate-in slide-in-from-bottom-2 fade-in-20 duration-200 origin-bottom">
                     <div className="px-3 py-2 border-b border-gray-100 mb-1">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Minha Conta</p>
                    </div>
                    <button onClick={onChangePassword} className="flex items-center w-full gap-3 px-3 py-2 text-sm text-gray-700 rounded-md hover:bg-blue-50 transition-colors">
                        <KeyRound size={16} /> <span>Alterar Senha</span>
                    </button>
                    <button onClick={onChangeKeyword} className="flex items-center w-full gap-3 px-3 py-2 text-sm text-gray-700 rounded-md hover:bg-blue-50 transition-colors">
                        <Shield size={16} /> <span>Palavra-Chave</span>
                    </button>
                    {user.is_super_user && Array.isArray(entities) && entities.length > 1 && (
                        <div className="my-1 pt-2 border-t border-gray-100 px-2 pb-1">
                            <label className="block px-1 mb-1.5 text-[10px] font-bold text-gray-400 uppercase">Trocar Entidade</label>
                            <select
                                value={user.entityId}
                                onChange={(e) => { onSwitchEntity(Number(e.target.value)); setIsMenuOpen(false); }}
                                disabled={isSwitchingEntity}
                                className="w-full p-1.5 text-xs bg-gray-50 border border-gray-200 rounded text-gray-700 focus:ring-blue-500"
                            >
                                {entities.map(entity => (
                                    <option key={entity.id} value={entity.id}>{entity.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="my-1 border-t border-gray-100"></div>
                    <button onClick={onLogout} className="flex items-center w-full gap-3 px-3 py-2 text-sm text-red-600 rounded-md hover:bg-red-50 transition-colors">
                        <LogOut size={16} /> <span className="font-medium">Sair</span>
                    </button>
                </div>
            )}
        </div>
    );
}

const Sidebar = ({ currentPage, setCurrentPage, onLogout, permissions, currentUser, onOpenChangePasswordModal, onOpenChangeKeywordModal, onSwitchEntity, entities, isSwitchingEntity, selectedCurrency, setSelectedCurrency, allCurrencies, unreadCount }) => {
    // --- DETECÇÃO DE TELA ---
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [logoUrl, setLogoUrl] = useState(null);
    const [openMenu, setOpenMenu] = useState(null);

    const toggleMenu = (menuName) => {
        setOpenMenu(prev => prev === menuName ? null : menuName);
    };

    useEffect(() => {
        const fetchLogo = async () => {
            if (currentUser && currentUser.entityId) {
                try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`/api/entities/${currentUser.entityId}/logo?t=${new Date().getTime()}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (response.ok) {
                        const blob = await response.blob();
                        setLogoUrl(URL.createObjectURL(blob));
                    } else { setLogoUrl(null); }
                } catch (error) { console.error("Erro logo:", error); setLogoUrl(null); }
            } else { setLogoUrl(null); }
        };
        fetchLogo();
        return () => { if (logoUrl) URL.revokeObjectURL(logoUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    // --- PREPARAÇÃO DE ITENS PARA MOBILE E DESKTOP ---

    // Cadastros
    const cadastroSubItems = useMemo(() => {
        if (isMobile) return []; // Oculto no Mobile
        if (currentUser.business_type === 'pet') {
            return [
                { id: 'pet-clients', label: 'Clientes e Pets', icon: Users, permission: 'clients_view' },
                { id: 'pet-suppliers', label: 'Fornecedores', icon: Briefcase, permission: 'clients_view' },
                { id: 'pet-service-table', label: 'Tab. Serviços Pet', icon: Table, permission: 'service_table_view' },
                { id: 'product-classifications', label: 'Classificação', icon: List, permission: 'product_classifications_view' },
                { id: 'products', label: 'Produtos', icon: Package, permission: 'products_view' }
            ];
        }
        if (currentUser.business_type === 'general' || currentUser.business_type === 'energia') {
             const items = [{ id: 'generic-clients', label: 'Clientes / Forn.', icon: Users, permission: 'clients_view' }];
             
             // ADICIONADO MENU DE COMISSÃO AQUI
             items.push({ id: 'commissions', label: 'Comissão', icon: BadgeDollarSign, permission: 'commissions_view' });
             
            if (currentUser.business_type === 'energia') {
                items.push({ id: 'service-table', label: 'Tab. Serviços', icon: Table, permission: 'service_table_view' });
            }
            // Adicionando Classificação e Produtos logo abaixo de Tabela de Serviços (ou do cliente)
            items.push({ id: 'product-classifications', label: 'Classificação', icon: List, permission: 'product_classifications_view' });
            items.push({ id: 'products', label: 'Produtos', icon: Package, permission: 'products_view' });
            
            return items;
        }
        return [];
    }, [currentUser.business_type, isMobile]);

    // Financeiro
    const financeSubItems = useMemo(() => {
        if (isMobile) return []; // Oculto no Mobile
        let items = [
            { id: 'accounts', label: 'Contas Banc.', icon: CreditCard, permission: 'accounts_view' },
            { id: 'transactions', label: 'Livro Caixa', icon: CalendarIcon, permission: 'transactions_view' },
        ];
        if (currentUser.business_type === 'general' || currentUser.business_type === 'energia') {
            items.splice(1, 0, 
                { id: 'contas-a-pagar', label: 'A Pagar', icon: ArrowDownCircle, permission: 'payables_view' },
                { id: 'contas-a-receber', label: 'A Receber', icon: ArrowUpCircle, permission: 'receivables_view' }
            );
        }
        return items;
    }, [currentUser.business_type, isMobile]);

    // --- MÓDULO: COMPRAS ---
    const comprasSubItems = useMemo(() => {
        if (isMobile) return []; 
        return [
            { id: 'payment-conditions', label: 'Condições de Pagamento', icon: Banknote, permission: 'payment_conditions_view' },
            { id: 'supplier-negotiation', label: 'Negociação Fornecedor', icon: Handshake, permission: 'supplier_negotiation_view' },
            { id: 'purchase-order', label: 'Pedido de Compra', icon: FilePlus, permission: 'purchase_order_view' },
            { id: 'invoice-entry', label: 'Consulta/Entrada NF', icon: FileDown, permission: 'invoice_entry_view' }
        ];
    }, [isMobile]);

    // --- MÓDULO: ESTOQUE (PERMISSÕES AJUSTADAS!) ---
    const estoqueSubItems = useMemo(() => {
        if (isMobile && !permissions?.inventory_mobile_collect && !permissions?.inventory_audit_view && !currentUser?.is_super_user) return [];
        
        return [
            { id: 'deposits', label: 'Cadastro Depósito', icon: Warehouse, permission: 'deposits_view' },
            { id: 'inventory-consult', label: 'Consulta Estoque', icon: Search, permission: 'inventory_consult_view' },
            { id: 'inventory-movements', label: 'Movimentações Depósitos', icon: Replace, permission: 'inventory_movements_view' },
            { id: 'inventory-write-off', label: 'Baixas', icon: Trash2, permission: 'inventory_write_off_view' },
            { id: 'inventory-audit', label: 'Inventário', icon: ClipboardCheck, permission: 'inventory_audit_view_or_collect' }
        ];
    }, [isMobile, permissions, currentUser]);

    // Comercial (Energia)
    const comercialSubItems = useMemo(() => {
        if (isMobile && currentUser.business_type !== 'energia') return [];
        if (currentUser.business_type === 'energia') {
            return [
                { id: 'comercial-status', label: 'Status', icon: Activity, permission: 'comercial_status_view' },
                { id: 'os-nsa', label: 'OS/NSA', icon: FileText, permission: 'comercial_os_view' },
                { id: 'schedule', label: 'Agenda', icon: CalendarIcon, permission: 'comercial_schedule_view' },
                { id: 'laudos-vencimento', label: 'Laudos Vencidos', icon: FileClock, permission: 'laudos_vencidos_view' },
            ];
        }
        return [];
    }, [currentUser.business_type, isMobile]); 

    // --- NOVO MÓDULO: CONTROLE DE FROTA (Com novo submenu de Cadastro) ---
    const frotaSubItems = useMemo(() => {
        return [
            { id: 'fleet-team', label: 'Equipes', icon: Users, permission: 'fleet_team_view' }, 
            { id: 'fleet-fuel', label: 'Combustível', icon: Fuel, permission: 'fleet_fuel_view' }
        ];
    }, []);

    // Configurações e Outros
    const otherNavItems = [
        ...(!currentUser?.is_client ? [{ id: 'meus-avisos', label: 'Avisos', icon: BellRing, permission: null }] : []),
        
        ...(isMobile ? [] : [
            { id: 'reports', label: 'Relatórios', icon: FileText, permission: 'reports_view' },
            { id: 'assistant', label: 'Assistente IA', icon: Bot, permission: 'assistant' },
            { id: 'monitoring', label: 'Monitoramento', icon: Activity, permission: 'is_super_user' },
            { id: 'systemLogs', label: 'Auditoria', icon: FileClock, permission: 'logs' },
        ])
    ];

    const settingsSubItems = useMemo(() => {
        if (isMobile) return [];
        const items = [{ id: 'settings', label: 'Geral', icon: Settings, permission: 'settings_view' }];
        items.push(
            { id: 'avisos', label: 'Avisos Admin', icon: Bell, permission: 'avisos_view' },
            { id: 'backup', label: 'Backup', icon: DatabaseBackup, permission: 'backup_view' } 
        );
        if (currentUser.business_type === 'energia') {
            items.push({ id: 'layout-settings', label: 'Layout Docs', icon: Palette, permission: 'layout_docs_view' }); 
            items.push({ id: 'report-layout', label: 'Layout Laudos', icon: FileSpreadsheet, permission: 'layout_laudos_view' }); 
        }
        return items;
    }, [currentUser.business_type, isMobile]);

    // --- FILTRAGEM FINAL DE PERMISSÕES ---
    const checkPerm = (item) => {
        if (item.permission === 'inventory_audit_view_or_collect') {
            return currentUser?.is_super_user || permissions?.inventory_audit_view || permissions?.inventory_mobile_collect;
        }
        return (permissions && permissions[item.permission]) || currentUser?.is_super_user || !item.permission;
    };

    // --- RENDERIZAÇÃO MOBILE ---
    if (isMobile) {
        const mobileNavItems = [
            ...(permissions?.dashboard || currentUser?.is_super_user ? [{ id: 'dashboard', label: 'Dash', icon: TrendingUp }] : []),
            ...comercialSubItems.filter(checkPerm), 
            ...estoqueSubItems.filter(i => i.id === 'inventory-audit' && checkPerm(i)),
            // INJETANDO O BOTÃO DE COMBUSTÍVEL AQUI NA BARRA INFERIOR MOBILE
            ...frotaSubItems.filter(i => i.id === 'fleet-fuel' && checkPerm(i)), 
            ...otherNavItems.filter(i => i.id === 'meus-avisos'), 
        ];

        return (
            <>
                <MobileHeader 
                    logoUrl={logoUrl} 
                    entityName={currentUser.entityName}
                    user={currentUser}
                    onLogout={onLogout}
                    selectedCurrency={selectedCurrency}
                    setSelectedCurrency={setSelectedCurrency}
                    allCurrencies={allCurrencies}
                    onSwitchEntity={onSwitchEntity}
                    entities={entities}
                    isSwitchingEntity={isSwitchingEntity}
                />
                
                <MobileBottomNav 
                    items={mobileNavItems} 
                    currentPage={currentPage} 
                    setCurrentPage={setCurrentPage} 
                    unreadCount={unreadCount}
                />
            </>
        );
    }

    // --- RENDERIZAÇÃO DESKTOP (ORIGINAL) ---
    const visibleCadastro = cadastroSubItems.filter(checkPerm);
    const visibleFinance = financeSubItems.filter(checkPerm);
    const visibleCompras = comprasSubItems.filter(checkPerm);
    const visibleEstoque = estoqueSubItems.filter(checkPerm);
    const visibleComercial = comercialSubItems.filter(checkPerm);
    const visibleFrota = frotaSubItems.filter(checkPerm);
    const visibleSettings = settingsSubItems.filter(checkPerm);
    const visibleOther = otherNavItems.filter(checkPerm);

    const NavItem = ({ item, isActive, onClick, hasSubmenu, isOpen }) => (
        <button onClick={onClick} className={`group flex items-center justify-between w-full px-3 py-2.5 my-1 rounded-lg transition-all duration-200 border border-transparent ${isActive ? 'bg-blue-600 text-white shadow-md shadow-blue-200 font-medium' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'} ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={item.disabled}>
            <div className="flex items-center gap-3">
                <item.icon className={`w-5 h-5 transition-colors ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`} />
                <span className="hidden md:block text-sm">{item.label}</span>
            </div>
            <div className="flex items-center">
                {item.id === 'meus-avisos' && unreadCount > 0 && <span className="flex items-center justify-center h-5 min-w-[20px] px-1.5 text-[10px] font-bold text-white bg-red-500 rounded-full mr-2 shadow-sm">{unreadCount}</span>}
                {hasSubmenu && <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />}
            </div>
        </button>
    );

    const SubItem = ({ item, isActive, onClick }) => (
        <button onClick={onClick} disabled={item.disabled} className={`flex items-center w-full px-3 py-2 text-sm rounded-md transition-all duration-200 group relative ${isActive ? 'text-blue-600 bg-blue-50 font-medium' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'} ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
            {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-4 bg-blue-500 rounded-r-full"></div>}
            <item.icon className={`w-4 h-4 mr-3 transition-colors ${isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-600'}`} />
            <span className="truncate">{item.label}</span>
        </button>
    );

    return (
        <aside className="flex flex-col w-16 md:w-64 bg-white h-screen shadow-xl border-r border-gray-200 font-inter transition-all duration-300 sticky top-0">
            <div className="flex flex-col items-center justify-center h-36 p-4 bg-white border-b border-gray-200">
                {logoUrl ? (
                    <div className="flex flex-col items-center animate-in fade-in zoom-in duration-500">
                        <img src={logoUrl} alt="Logo" className="w-24 h-24 object-contain mb-2" />
                        <span className="hidden md:block font-bold text-gray-800 text-center leading-tight px-2">{currentUser.entityName}</span>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center gap-3">
                        <div className="p-3 bg-blue-600 rounded-xl shadow-lg shadow-blue-200"><DollarSign className="w-8 h-8 text-white" /></div>
                        <span className="hidden md:block text-xl font-bold text-gray-800 tracking-tight">DFinAI</span>
                    </div>
                )}
            </div>

            <CurrencyFilter currentUser={currentUser} allCurrencies={allCurrencies} selectedCurrency={selectedCurrency} setSelectedCurrency={setSelectedCurrency} />

            <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                {permissions && permissions.dashboard && <NavItem item={{ id: 'dashboard', label: 'Dashboard', icon: TrendingUp }} isActive={currentPage === 'dashboard'} onClick={() => setCurrentPage('dashboard')} />}

                {visibleCadastro.length > 0 && (
                    <div className="pt-2">
                        <div className="hidden md:block px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">Operacional</div>
                        <NavItem item={{ label: 'Cadastros', icon: ClipboardList }} isActive={visibleCadastro.some(i => i.id === currentPage)} hasSubmenu={true} isOpen={openMenu === 'cadastro'} onClick={() => toggleMenu('cadastro')} />
                        {openMenu === 'cadastro' && <div className="ml-3 pl-3 border-l border-gray-200 space-y-1 mt-1 mb-3 animate-in slide-in-from-top-2 duration-200">{visibleCadastro.map(item => <SubItem key={item.id} item={item} isActive={currentPage === item.id} onClick={() => setCurrentPage(item.id)} />)}</div>}
                    </div>
                )}

                {visibleFinance.length > 0 && (
                    <div>
                        <NavItem item={{ label: 'Financeiro', icon: DollarSign }} isActive={visibleFinance.some(i => i.id === currentPage)} hasSubmenu={true} isOpen={openMenu === 'finance'} onClick={() => toggleMenu('finance')} />
                        {openMenu === 'finance' && <div className="ml-3 pl-3 border-l border-gray-200 space-y-1 mt-1 mb-3 animate-in slide-in-from-top-2 duration-200">{visibleFinance.map(item => <SubItem key={item.id} item={item} isActive={currentPage === item.id} onClick={() => !item.disabled && setCurrentPage(item.id)} />)}</div>}
                    </div>
                )}

                {visibleCompras.length > 0 && (
                    <div>
                        <NavItem item={{ label: 'Compras', icon: ShoppingCart }} isActive={visibleCompras.some(i => i.id === currentPage)} hasSubmenu={true} isOpen={openMenu === 'compras'} onClick={() => toggleMenu('compras')} />
                        {openMenu === 'compras' && <div className="ml-3 pl-3 border-l border-gray-200 space-y-1 mt-1 mb-3 animate-in slide-in-from-top-2 duration-200">{visibleCompras.map(item => <SubItem key={item.id} item={item} isActive={currentPage === item.id} onClick={() => !item.disabled && setCurrentPage(item.id)} />)}</div>}
                    </div>
                )}

                {visibleEstoque.length > 0 && (
                    <div>
                        <NavItem item={{ label: 'Estoque', icon: Package }} isActive={visibleEstoque.some(i => i.id === currentPage)} hasSubmenu={true} isOpen={openMenu === 'estoque'} onClick={() => toggleMenu('estoque')} />
                        {openMenu === 'estoque' && <div className="ml-3 pl-3 border-l border-gray-200 space-y-1 mt-1 mb-3 animate-in slide-in-from-top-2 duration-200">{visibleEstoque.map(item => <SubItem key={item.id} item={item} isActive={currentPage === item.id} onClick={() => !item.disabled && setCurrentPage(item.id)} />)}</div>}
                    </div>
                )}

                {visibleComercial.length > 0 && (
                    <div>
                         <NavItem item={{ label: 'Comercial', icon: Briefcase }} isActive={visibleComercial.some(i => i.id === currentPage)} hasSubmenu={true} isOpen={openMenu === 'comercial'} onClick={() => toggleMenu('comercial')} />
                        {openMenu === 'comercial' && <div className="ml-3 pl-3 border-l border-gray-200 space-y-1 mt-1 mb-3 animate-in slide-in-from-top-2 duration-200">{visibleComercial.map(item => <SubItem key={item.id} item={item} isActive={currentPage === item.id} onClick={() => !item.disabled && setCurrentPage(item.id)} />)}</div>}
                    </div>
                )}

                {/* --- NOVO MÓDULO: FROTA --- */}
                {visibleFrota.length > 0 && (
                    <div>
                         <NavItem item={{ label: 'Controle de Frota', icon: Truck }} isActive={visibleFrota.some(i => i.id === currentPage)} hasSubmenu={true} isOpen={openMenu === 'frota'} onClick={() => toggleMenu('frota')} />
                        {openMenu === 'frota' && <div className="ml-3 pl-3 border-l border-gray-200 space-y-1 mt-1 mb-3 animate-in slide-in-from-top-2 duration-200">{visibleFrota.map(item => <SubItem key={item.id} item={item} isActive={currentPage === item.id} onClick={() => !item.disabled && setCurrentPage(item.id)} />)}</div>}
                    </div>
                )}

                <div className="pt-4 mt-2 border-t border-gray-200">
                    <div className="hidden md:block px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">Ferramentas</div>
                    {visibleOther.map(item => <NavItem key={item.id} item={item} isActive={currentPage === item.id} onClick={() => setCurrentPage(item.id)} />)}
                </div>

                {visibleSettings.length > 0 && (
                    <div>
                        <NavItem item={{ label: 'Configurações', icon: Settings }} isActive={visibleSettings.some(i => i.id === currentPage)} hasSubmenu={true} isOpen={openMenu === 'settings'} onClick={() => toggleMenu('settings')} />
                        {openMenu === 'settings' && <div className="ml-3 pl-3 border-l border-gray-200 space-y-1 mt-1 mb-3 animate-in slide-in-from-top-2 duration-200">{visibleSettings.map(item => <SubItem key={item.id} item={item} isActive={currentPage === item.id} onClick={() => !item.disabled && setCurrentPage(item.id)} />)}</div>}
                    </div>
                )}
            </nav>

            <UserProfile user={currentUser} onLogout={onLogout} onChangePassword={onOpenChangePasswordModal} onChangeKeyword={onOpenChangeKeywordModal} onSwitchEntity={onSwitchEntity} entities={entities} isSwitchingEntity={isSwitchingEntity} />
        </aside>
    );
};

export default Sidebar;